import React, { useState, useRef, useEffect } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Spinner,
  Button,
  Alert,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import { postRequest, getRequest } from "../../components/Common/Utils.js";

export default function WarehouseManagementUpdate() {
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationBlocked, setLocationBlocked] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const passedRow = location.state?.row;

  const [warehouseData, setWarehouseData] = useState({
    name: "",
    address: "",
    latitude: "",
    longitude: "",
    phone: "",
    capacity: "",
    country: "",
    country_code: "",
    city: "",
    state: "",
    postCode: "",
  });

  // Set data from passed state
  useEffect(() => {
    if (passedRow) {
      setWarehouseData({
        name: passedRow.name || "",
        address: passedRow.address || "",
        latitude: passedRow.location?.coordinates?.[1]?.toString() || "",
        longitude: passedRow.location?.coordinates?.[0]?.toString() || "",
        phone: passedRow.phone || "",
        capacity: passedRow.capacity || "",
        country: passedRow.country || "",
        country_code: passedRow.country_code || "",
        city: passedRow.city || "",
        state: passedRow.state || "",
        postCode: passedRow.postCode || "",
      });
    }
  }, [passedRow]);

  // Initialize Google Maps
  useEffect(() => {
    if (showMap && mapRef.current && !map) {
      const google = window.google;
      const initialLat = parseFloat(warehouseData.latitude) || 0;
      const initialLng = parseFloat(warehouseData.longitude) || 0;

      const newMap = new google.maps.Map(mapRef.current, {
        center: { lat: initialLat, lng: initialLng },
        zoom: initialLat && initialLng ? 15 : 2,
      });

      setMap(newMap);

      if (warehouseData.latitude && warehouseData.longitude) {
        const newMarker = new google.maps.Marker({
          position: { lat: initialLat, lng: initialLng },
          map: newMap,
        });
        setMarker(newMarker);
      }

      newMap.addListener("click", (e) => {
        setWarehouseData((prev) => ({
          ...prev,
          latitude: e.latLng.lat().toString(),
          longitude: e.latLng.lng().toString(),
        }));

        if (marker) marker.setMap(null);

        const newMarker = new google.maps.Marker({
          position: e.latLng,
          map: newMap,
        });
        setMarker(newMarker);
      });
    }
  }, [showMap, map, marker, warehouseData.latitude, warehouseData.longitude]);

  // Autocomplete init
  useEffect(() => {
    if (window.google && !autocompleteRef.current) {
      const input = document.getElementById("address-input");
      if (input) {
        const autocomplete = new window.google.maps.places.Autocomplete(input, {
          types: ["geocode"],
        });
        autocompleteRef.current = autocomplete;

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            // Extract address components
            let country = "";
            let country_code = "";
            let city = "";
            let state = "";
            let postCode = "";

            for (const component of place.address_components) {
              const componentType = component.types[0];

              if (componentType === "country") {
                country = component.long_name;
                country_code = component.short_name;
              }

              if (componentType === "locality") {
                city = component.long_name;
              }

              if (componentType === "administrative_area_level_1") {
                state = component.long_name;
              }

              if (componentType === "postal_code") {
                postCode = component.long_name;
              }
            }

            setWarehouseData((prev) => ({
              ...prev,
              address: place.formatted_address,
              latitude: place.geometry.location.lat().toString(),
              longitude: place.geometry.location.lng().toString(),
              country,
              country_code,
              city,
              state,
              postCode,
            }));

            if (map) {
              map.setCenter(place.geometry.location);
              map.setZoom(15);

              if (marker) marker.setMap(null);

              const newMarker = new window.google.maps.Marker({
                position: place.geometry.location,
                map: map,
              });
              setMarker(newMarker);
            }
          }
        });
      }
    }
  }, [map, marker]);

  const checkGeolocationPermission = async () => {
    if (!navigator.permissions) return "unknown";
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "geolocation",
      });
      return permissionStatus.state;
    } catch (error) {
      return "unknown";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      warehouseId: id,
      name: warehouseData.name,
      address: warehouseData.address,
      latitude: parseFloat(warehouseData.latitude),
      longitude: parseFloat(warehouseData.longitude),
      phone: parseInt(warehouseData.phone),
      capacity: parseInt(warehouseData.capacity),
      country: warehouseData.country,
      country_code: warehouseData.country_code,
      city: warehouseData.city,
      state: warehouseData.state,
      postCode: warehouseData.postCode,
    };

    try {
      const response = await postRequest(`/warehouse/update`, payload);
      setLoading(false);

      if (response.status) {
        toast.success(response.message);
        setTimeout(() => {
          history.push("/warehouse-list");
        }, 1000);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      setLoading(false);
      toast.error("Error updating warehouse");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWarehouseData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleMap = () => {
    setShowMap(!showMap);
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <Row>
        <Col className="10">
          <div className="page-title-box">
            <h4>Warehouse Management</h4>
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item active">Warehouse Management</li>
              <li className="breadcrumb-item">Update Warehouse</li>
            </ol>
          </div>
        </Col>

        <div class="col-md-2 text-end">
          <button
            type="button"
            onClick={() => history.goBack()}
            className="btn btn-primary btn-lg"
          >
            <i class="fas fa-chevron-left"></i> Back
          </button>
        </div>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            {loading ? (
              <CardBody style={{ height: "100px" }}>
                <Spinner
                  color="info"
                  style={{
                    height: "4rem",
                    width: "4rem",
                    position: "absolute",
                    left: "50%",
                  }}
                >
                  Loading...
                </Spinner>
              </CardBody>
            ) : (
              <CardBody>
                <h4 className="card-title">Update Warehouse</h4>

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col lg={12}>
                      <div className="mt-5 mt-lg-4">
                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Warehouse Name
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="name"
                              onChange={handleChange}
                              value={warehouseData.name}
                              className="form-control"
                              placeholder="Enter warehouse name"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Address
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              id="address-input"
                              name="address"
                              onChange={handleChange}
                              value={warehouseData.address}
                              className="form-control"
                              placeholder="Enter full street address"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Country
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="country"
                              onChange={handleChange}
                              value={warehouseData.country}
                              className="form-control"
                              placeholder="Enter country name"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Country Code
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="country_code"
                              onChange={handleChange}
                              value={warehouseData.country_code}
                              className="form-control"
                              placeholder="Enter country code"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            City
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="city"
                              onChange={handleChange}
                              value={warehouseData.city}
                              className="form-control"
                              placeholder="Enter city name"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            State
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="state"
                              onChange={handleChange}
                              value={warehouseData.state}
                              className="form-control"
                              placeholder="Enter state"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Postal Code
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="text"
                              name="postCode"
                              onChange={handleChange}
                              value={warehouseData.postCode}
                              className="form-control"
                              placeholder="Enter postal/zip code"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Location
                          </label>
                          <div className="col-sm-6">
                            {/* <div className="d-flex mb-2 gap-2">
                              <Button color="secondary" onClick={toggleMap}>
                                {showMap ? "Hide Map" : "Show Map"}
                              </Button>
                            </div>

                            {locationBlocked && (
                              <Alert color="danger">
                                <strong>Location access blocked</strong>
                                <ol className="small mt-2 mb-0">
                                  <li>
                                    Click the lock icon in the address bar
                                  </li>
                                  <li>Choose "Site settings"</li>
                                  <li>Set "Location" to "Allow"</li>
                                  <li>Refresh the page</li>
                                </ol>
                              </Alert>
                            )}

                            {showMap && (
                              <div
                                ref={mapRef}
                                style={{
                                  height: "300px",
                                  width: "100%",
                                  marginBottom: "1rem",
                                }}
                              />
                            )} */}

                            <div className="row">
                              <div className="col-md-6">
                                <label>Latitude</label>
                                <input
                                  type="number"
                                  step="any"
                                  name="latitude"
                                  onChange={handleChange}
                                  value={warehouseData.latitude}
                                  className="form-control"
                                  required
                                  placeholder="Click map or search address"
                                />
                              </div>
                              <div className="col-md-6">
                                <label>Longitude</label>
                                <input
                                  type="number"
                                  step="any"
                                  name="longitude"
                                  onChange={handleChange}
                                  value={warehouseData.longitude}
                                  className="form-control"
                                  required
                                  placeholder="Click map or search address"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Phone Number
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="number"
                              name="phone"
                              onChange={handleChange}
                              value={warehouseData.phone}
                              className="form-control"
                              placeholder="Enter contact number"
                              required
                            />
                          </div>
                        </div>

                        <div className="row mb-4">
                          <label className="col-sm-3 col-form-label">
                            Capacity
                          </label>
                          <div className="col-sm-6">
                            <input
                              type="number"
                              name="capacity"
                              onChange={handleChange}
                              value={warehouseData.capacity}
                              className="form-control"
                              placeholder="Enter storage capacity in units"
                              required
                            />
                          </div>
                        </div>

                        <div className="mt-5">
                          <div className="row justify-content-end">
                            <div className="col-sm-9">
                              <button
                                type="submit"
                                className="btn btn-primary w-md"
                                disabled={loading}
                              >
                                {loading ? <Spinner size="sm" /> : "Submit"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            )}
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
}
